import {
  SessionStore,
  selectIsCountryCanada,
  selectIsPaymentLinks,
  selectVirtualOtherwisePrimaryTenantId,
} from '@arc/unified-payments-frontend/shared-stores';
import { createSelector } from '@ngrx/store';
import { selectAllowCardOverride } from './pay-on-account-card-override.selectors';
import {
  Country,
  PayOnAccountPaymentPreviewV1Request,
  PaymentSourceType,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  selectPaymentDate,
  selectPaymentDetails,
  selectPaymentPreview,
  selectSelectedPaymentAccount,
} from '../reducers';
import {
  selectBillingAddressCountry,
  selectCustomTemplateData,
  selectPaymentReference,
} from './pay-on-account.selectors';
import { getFormattedUTCDate } from '@arc/unified-payments-frontend/date-utils';

export const selectCreatePayOnAccountPaymentPreviewRequestData = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  SessionStore,
  selectAllowCardOverride,
  selectIsPaymentLinks,
  selectCustomTemplateData,
  (
    tenantId,
    session,
    allowCardOverride,
    isPaymentLinks,
    customTemplateData,
  ) => {
    const requestData: {
      tenantId: string;
      paymentPreviewRequest: PayOnAccountPaymentPreviewV1Request;
    } = {
      tenantId,
      paymentPreviewRequest: {
        payment: {
          account: {
            buyerAccountIdentifier: session.buyerAccountIdentifier,
          },
          currency: session.currencyCode,
          amount: customTemplateData?.amount ?? 0,
          note: '',
        },
        paymentInitiator: {
          paymentSourceType: isPaymentLinks
            ? session.paymentSourceType
            : PaymentSourceType.PayOnAccount,
          paymentSource: session.originatingApplication,
          embeddedPaymentInitiationSource:
            session.embeddedPaymentInitiationSource,
        },
        externalUser: {
          burn: session.externalUserUrn ? session.externalUserUrn : null,
        },
        overrideCardRestrictions: allowCardOverride,
      },
    };

    return requestData;
  },
);

export const selectUpdatePaymentPreviewSurchargeRequest = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  SessionStore,
  selectPaymentDetails,
  selectSelectedPaymentAccount,
  selectPaymentDate,
  selectPaymentReference,
  selectAllowCardOverride,
  selectIsPaymentLinks,
  (
    tenantId,
    session,
    paymentDetails,
    paymentAccount,
    validatedPaymentDate,
    paymentReference,
    allowCardOverride,
    isPaymentLinks,
  ) => {
    const requestData: {
      tenantId: string;
      paymentPreviewRequest: PayOnAccountPaymentPreviewV1Request;
    } = {
      tenantId,
      paymentPreviewRequest: {
        payment: {
          account: {
            buyerAccountIdentifier: paymentDetails.buyerAccount.accountNumber,
          },
          currency: session.currencyCode,
          amount: paymentDetails.amount,
          note: paymentDetails.paymentNotes ?? '',
          reference: paymentDetails.referenceNumber,
          referenceDate: paymentDetails.referenceDate,
          ...(paymentDetails.customTemplateData && {
            customTemplateData: paymentDetails.customTemplateData,
          }),
        },
        ...(paymentAccount
          ? {
              paymentAccount: {
                burn: paymentAccount.paymentAccountIdentifierBurn,
              },
            }
          : {}),
        paymentIntendedDate: getFormattedUTCDate(paymentDetails.paymentDate),
        paymentSchedule: {
          paymentDate: getFormattedUTCDate(validatedPaymentDate),
        },
        paymentInitiator: {
          paymentSourceType: isPaymentLinks
            ? (session.paymentSourceType as PaymentSourceType)
            : PaymentSourceType.PayOnAccount,
          paymentSource: session.originatingApplication,
          embeddedPaymentInitiationSource:
            session.embeddedPaymentInitiationSource,
        },
        externalUser: {
          burn: session.externalUserUrn ? session.externalUserUrn : null,
        },
        ...(paymentReference != null && {
          paymentReference: {
            burn: paymentReference.toString(),
          },
        }),
        overrideCardRestrictions: allowCardOverride,
      },
    };

    return requestData;
  },
);

export const selectPoaNewPaymentReferencePaymentPreviewRequest = createSelector(
  selectUpdatePaymentPreviewSurchargeRequest,
  (request) => {
    request.paymentPreviewRequest.paymentReference = null;
    return request;
  },
);

export const selectSurchargePercentage = createSelector(
  selectPaymentPreview,
  (paymentPreview) => {
    if (paymentPreview == null) {
      return null;
    }

    return (
      paymentPreview.payments?.[0]?.payment.surchargeTransactionFeePercent ??
      null
    );
  },
);

export const selectIsSurchargeFeePercentageVisible = createSelector(
  selectSurchargePercentage,
  selectIsCountryCanada,
  selectBillingAddressCountry,
  (surchargeFeePercentage, isCountryCanada, billingAddressCountry) => {
    return (
      surchargeFeePercentage > 0 &&
      (isCountryCanada || billingAddressCountry === Country.CA)
    );
  },
);

export const selectExternalUserUrn = createSelector(
  selectPaymentPreview,
  (preview) => preview?.payments?.[0]?.externalUser?.burn,
);
